import type { PuntoTasa } from "./tipos"

const ANCHO = 96
const ALTO = 28
const MARGEN = 4

/**
 * La serie de una tarjeta, sin ejes: sólo la forma de las últimas semanas.
 *
 * ⚠ **Decorativo para el lector de pantalla** (`aria-hidden`): la cifra está escrita al
 * lado, y la historia entera en la tabla del historial. Un minigráfico nunca es la
 * única forma de leer un valor.
 *
 * ⚠ **La línea va en gris y sólo el último punto en el acento**, como pide la guía de
 * visualización: el color se reserva para «hoy», que es lo que la tarjeta dice.
 *
 * Con menos de dos puntos no hay línea que dibujar, y no se dibuja un punto suelto.
 */
export function Minigrafico({ puntos }: { puntos: readonly PuntoTasa[] }) {
  if (puntos.length < 2) return null
  const valores = puntos.map((p) => Number(p.bs_por_unidad))
  const minimo = Math.min(...valores)
  const maximo = Math.max(...valores)
  const plano = maximo - minimo < 1e-9
  const x = (i: number) => MARGEN + (i * (ANCHO - 2 * MARGEN)) / (valores.length - 1)
  // Una serie plana va por el medio: pegada abajo parecería una caída.
  const y = (v: number) =>
    plano ? ALTO / 2 : ALTO - MARGEN - ((v - minimo) * (ALTO - 2 * MARGEN)) / (maximo - minimo)
  const trazo = valores
    .map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join("")
  const ultimo = valores.length - 1

  return (
    <svg
      aria-hidden={true}
      viewBox={`0 0 ${ANCHO} ${ALTO}`}
      width={ANCHO}
      height={ALTO}
      className="block shrink-0 overflow-visible"
    >
      <path
        d={trazo}
        fill="none"
        stroke="var(--fg-muted)"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={x(ultimo)}
        cy={y(valores[ultimo] ?? 0)}
        r={3}
        fill="var(--primary)"
        stroke="var(--surface)"
        strokeWidth={2}
      />
    </svg>
  )
}
